import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_LOADING,
  STATUS_ICON_RUN,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_PAUSE
} from '@/common/utils/statuses';

type VolumeType = {
  title: string;
  icon: string;
};

export const MIN_VOLUME_GIB = 1;
export const MAX_VOLUME_TIB = 512;

export enum VOLUME_STATUSES {
  ATTACHING = 'attaching',
  AVAILABLE = 'available',
  AWAITING_TRANSFER = 'awaiting-transfer',
  BACKING_UP = 'backing-up',
  CREATING = 'creating',
  DELETED = 'deleted',
  DELETING = 'deleting',
  DETACHING = 'detaching',
  DOWNLOADING = 'downloading',
  ERROR = 'error',
  ERROR_BACKING_UP = 'error_backing-up',
  ERROR_DELETING = 'error_deleting',
  ERROR_EXTENDING = 'error_extending',
  ERROR_RESTORING = 'error_restoring',
  EXTENDING = 'extending',
  IN_USE = 'in-use',
  MAINTENANCE = 'maintenance',
  RESERVED = 'reserved',
  RESTORING_BACKUP = 'restoring-backup',
  RETYPING = 'retyping',
  REVERTING = 'reverting',
  UPLOADING = 'uploading',
}

export enum VOLUME_SNAPSHOTS_STATUSES {
  AVAILABLE = 'available',
  BACKING_UP = 'backing_up',
  CREATING = 'creating',
  DELETING = 'deleting',
  DELETED = 'deleted',
  ERROR = 'error',
  ERROR_DELETING = 'error_deleting',
  RESTORING = 'restoring',
  UNMANAGING = 'unmanaging',
  UPDATING = 'updating',
  DELETE_PENDING = 'delete_pending',
  DELETE_PAUSED = 'delete_paused',
}

export const VOLUME_STATUSES_GROUPS: Record<string, (string | VOLUME_STATUSES)[]> = {
  Available: [VOLUME_STATUSES.AVAILABLE],
  InUse: [VOLUME_STATUSES.IN_USE, VOLUME_STATUSES.RESERVED],
  InProgress: [
    VOLUME_STATUSES.BACKING_UP,
    VOLUME_STATUSES.EXTENDING,
    VOLUME_STATUSES.DELETING,
    VOLUME_STATUSES.DETACHING,
    VOLUME_STATUSES.ATTACHING,
    VOLUME_STATUSES.CREATING,
    VOLUME_STATUSES.RETYPING,
    VOLUME_STATUSES.REVERTING,
    VOLUME_STATUSES.AWAITING_TRANSFER,
    VOLUME_STATUSES.MAINTENANCE,
    VOLUME_STATUSES.RESTORING_BACKUP,
    VOLUME_STATUSES.UPLOADING
  ],
  Error: [
    VOLUME_STATUSES.ERROR,
    VOLUME_STATUSES.ERROR_EXTENDING,
    VOLUME_STATUSES.ERROR_DELETING,
    VOLUME_STATUSES.ERROR_BACKING_UP,
    VOLUME_STATUSES.ERROR_RESTORING
  ],
  CanCreateBackup: [VOLUME_STATUSES.IN_USE, VOLUME_STATUSES.AVAILABLE],
};

export const VOLUME_TYPES: Record<string, VolumeType> = computedLocale(() => ({
  DISK: {
    title: i18n.t('Disk'),
    icon: 'i-local-backup-o--24',
  },
  CD_ROM: {
    title: i18n.t('CD-ROM'),
    icon: 'i-volume-iso-o--24',
  }
}));

type VolumeStatusesIconsMap = Record<VOLUME_STATUSES, StatusData>

export const VOLUME_STATUS_MAP = computedLocale<VolumeStatusesIconsMap>(() => ({
  [VOLUME_STATUSES.AWAITING_TRANSFER]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Awaiting transfer')
  },
  [VOLUME_STATUSES.BACKING_UP]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Backing up')
  },
  [VOLUME_STATUSES.ERROR_BACKING_UP]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error backing up')
  },
  [VOLUME_STATUSES.IN_USE]: {
    icon: STATUS_ICON_RUN,
    text: i18n.t('In use')
  },
  [VOLUME_STATUSES.ATTACHING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Attaching')
  },
  [VOLUME_STATUSES.AVAILABLE]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Available')
  },
  [VOLUME_STATUSES.CREATING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Creating')
  },
  [VOLUME_STATUSES.DELETED]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Deleted')
  },
  [VOLUME_STATUSES.DELETING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Deleting')
  },
  [VOLUME_STATUSES.DETACHING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Detaching')
  },
  [VOLUME_STATUSES.DOWNLOADING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Downloading')
  },
  [VOLUME_STATUSES.ERROR]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error')
  },
  [VOLUME_STATUSES.ERROR_DELETING]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error deleting')
  },
  [VOLUME_STATUSES.ERROR_EXTENDING]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Failed to expand')
  },
  [VOLUME_STATUSES.ERROR_RESTORING]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error restoring')
  },
  [VOLUME_STATUSES.EXTENDING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Expanding')
  },
  [VOLUME_STATUSES.MAINTENANCE]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Maintenance')
  },
  [VOLUME_STATUSES.RESERVED]: {
    icon: STATUS_ICON_RUN,
    text: i18n.t('Reserved')
  },
  [VOLUME_STATUSES.RESTORING_BACKUP]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Restoring backup')
  },
  [VOLUME_STATUSES.RETYPING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Changing storage policy')
  },
  [VOLUME_STATUSES.REVERTING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Reverting to snapshot')
  },
  [VOLUME_STATUSES.UPLOADING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Uploading')
  },
}));

export const VOLUME_SNAPSHOTS_TEXT: Record<VOLUME_SNAPSHOTS_STATUSES, string> = computedLocale(() => ({
  [VOLUME_SNAPSHOTS_STATUSES.AVAILABLE]: i18n.t('Available'),
  [VOLUME_SNAPSHOTS_STATUSES.BACKING_UP]: i18n.t('Backing up'),
  [VOLUME_SNAPSHOTS_STATUSES.CREATING]: i18n.t('Creating'),
  [VOLUME_SNAPSHOTS_STATUSES.DELETED]: i18n.t('Deleted'),
  [VOLUME_SNAPSHOTS_STATUSES.DELETING]: i18n.t('Deleting'),
  [VOLUME_SNAPSHOTS_STATUSES.ERROR_DELETING]: i18n.t('Error deleting'),
  [VOLUME_SNAPSHOTS_STATUSES.ERROR]: i18n.t('Error'),
  [VOLUME_SNAPSHOTS_STATUSES.RESTORING]: i18n.t('Restoring'),
  [VOLUME_SNAPSHOTS_STATUSES.UNMANAGING]: i18n.t('Unmanaging'),
  [VOLUME_SNAPSHOTS_STATUSES.UPDATING]: i18n.t('Updating'),
  [VOLUME_SNAPSHOTS_STATUSES.DELETE_PENDING]: i18n.t('Pending deletion'),
  [VOLUME_SNAPSHOTS_STATUSES.DELETE_PAUSED]: i18n.t('Deletion paused'),
}));

type VolumeSnapshotsStatusMap = Record<VOLUME_SNAPSHOTS_STATUSES, StatusData>

export const VOLUME_SNAPSHOTS_STATUS_MAP = computedLocale<VolumeSnapshotsStatusMap>(() => ({
  [VOLUME_SNAPSHOTS_STATUSES.AVAILABLE]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Available')
  },
  [VOLUME_SNAPSHOTS_STATUSES.BACKING_UP]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Backing up')
  },
  [VOLUME_SNAPSHOTS_STATUSES.CREATING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Creating')
  },
  [VOLUME_SNAPSHOTS_STATUSES.DELETED]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Deleted')
  },
  [VOLUME_SNAPSHOTS_STATUSES.DELETING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Deleting')
  },
  [VOLUME_SNAPSHOTS_STATUSES.ERROR_DELETING]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error deleting')
  },
  [VOLUME_SNAPSHOTS_STATUSES.ERROR]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Error')
  },
  [VOLUME_SNAPSHOTS_STATUSES.RESTORING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Restoring')
  },
  [VOLUME_SNAPSHOTS_STATUSES.UNMANAGING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Unmanaging')
  },
  [VOLUME_SNAPSHOTS_STATUSES.UPDATING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Updating')
  },
  [VOLUME_SNAPSHOTS_STATUSES.DELETE_PENDING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Pending deletion')
  },
  [VOLUME_SNAPSHOTS_STATUSES.DELETE_PAUSED]: {
    icon: STATUS_ICON_PAUSE,
    text: i18n.t('Deletion paused')
  }
}));

export const VOLUME_FILTER_LIST: VOLUME_STATUSES[] = [
  VOLUME_STATUSES.AVAILABLE,
  VOLUME_STATUSES.IN_USE,
  VOLUME_STATUSES.BACKING_UP,
  VOLUME_STATUSES.RESTORING_BACKUP,
  VOLUME_STATUSES.RESERVED,
  VOLUME_STATUSES.CREATING,
  VOLUME_STATUSES.ATTACHING,
  VOLUME_STATUSES.MAINTENANCE,
  VOLUME_STATUSES.DELETING,
  VOLUME_STATUSES.ERROR,
  VOLUME_STATUSES.ERROR_DELETING,
  VOLUME_STATUSES.ERROR_EXTENDING
];

export const VOLUME_INTERNAL_BACKEND = 'vstorage';

export const VOLUME_LOCAL_BACKEND = 'vstorage_local';

export const VOLUME_INTERNAL_BACKENDS: string[] = [VOLUME_INTERNAL_BACKEND, VOLUME_LOCAL_BACKEND];

export const isInternalVolumeBackend = (backend?: string | null): boolean => VOLUME_INTERNAL_BACKENDS
  .includes(backend || '');
